import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { adminService } from "../../services/adminService";
import {
  buildWaitlistFilters,
  triggerBlobDownload,
} from "../utils/waitlistQuery";

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_FILTERS = {
  query: "",
  paid: "",
  paymentStatus: "",
  messageTemplate: "",
};

const VIEW_OPTIONS = [
  { value: "waitlist", label: "Waitlist" },
  { value: "whatsapp", label: "WhatsApp Outreach" },
];

const WAITLIST_DATE_SORT_COLUMNS = {
  created_at: (item) => item.created_at,
  paid_at: (item) => item.paid_at,
  first_seen_at: (item) => item.first_seen_at || item.created_at,
  last_seen_at: (item) => item.last_seen_at || item.created_at,
};

const ANSWER_SUMMARY_FIELDS = [
  { label: "Target Role", aliases: ["target_role", "targetRole", "Target Role"] },
  { label: "Q1Label", aliases: ["q1_label", "q1Label", "Q1Label", "Q1 Label"] },
  {
    label: "Diagnostic Path Label",
    aliases: ["diagnostic_path_label", "diagnosticPathLabel", "Diagnostic Path Label"],
  },
  { label: "Decisions", aliases: ["decisions", "Decisions"] },
  { label: "Door", aliases: ["door", "Door"] },
];

const RESULT_SUMMARY_FIELDS = [
  { label: "Title", aliases: ["title", "Title"] },
  { label: "Concept", aliases: ["concept", "Concept"] },
  { label: "Pain", aliases: ["pain", "Pain"] },
  { label: "Program", aliases: ["program", "Program"] },
  { label: "Summary", aliases: ["summary", "Summary"] },
];

const ANSWER_RESPONSE_ALIASES = ["response_text", "responseText", "Response Text"];
const ANSWER_LABEL_ALIASES = ["label", "Label"];

const Waitlist = () => {
  const [activeView, setActiveView] = useState("waitlist");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageInfo, setPageInfo] = useState({ totalPages: null, totalCount: null });
  const [emailStatus, setEmailStatus] = useState({});
  const [deleteStatus, setDeleteStatus] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null);
  const [diagnosticModal, setDiagnosticModal] = useState({
    open: false,
    item: null,
    rowKey: null,
    session: null,
    loading: false,
    error: "",
  });
  const [promotionFlowModal, setPromotionFlowModal] = useState({
    open: false,
    item: null,
    session: null,
    loading: false,
    error: "",
  });
  const [whatsappItems, setWhatsappItems] = useState([]);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappError, setWhatsappError] = useState("");
  const [whatsappPageInfo, setWhatsappPageInfo] = useState({
    totalPages: null,
    totalCount: null,
  });
  const [whatsappStats, setWhatsappStats] = useState({
    totalRecords: 0,
    validPhoneCount: 0,
    invalidPhoneCount: 0,
  });
  const [downloadStatus, setDownloadStatus] = useState({
    csv: "idle",
    vcf: "idle",
    message: "",
  });
  const [waitlistSort, setWaitlistSort] = useState({
    key: "created_at",
    direction: "desc",
  });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [debouncedMessageTemplate, setDebouncedMessageTemplate] = useState("");

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedMessageTemplate(filters.messageTemplate);
    }, 350);
    return () => window.clearTimeout(timeoutId);
  }, [filters.messageTemplate]);

  const loadWaitlist = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const offset = (page - 1) * pageSize;
      const params = { limit: pageSize, offset };
      const waitlistFilters = buildWaitlistFilters(filters);

      if (waitlistFilters.q) params.q = waitlistFilters.q;
      if (waitlistFilters.paid) params.paid = waitlistFilters.paid;

      const payload = await adminService.getWaitlist(params);
      const normalized = normalizeWaitlistResponse(payload, page, pageSize);
      setItems(normalized.items);
      setPageInfo({
        totalPages: normalized.totalPages,
        totalCount: normalized.totalCount,
      });
    } catch (err) {
      console.error(err);
      setError("Failed to fetch waitlist.");
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  const loadWhatsappWaitlist = useCallback(async () => {
    setWhatsappLoading(true);
    setWhatsappError("");

    try {
      const offset = (page - 1) * pageSize;
      const params = {
        ...buildWaitlistFilters({
          ...filters,
          messageTemplate: debouncedMessageTemplate,
        }),
        limit: pageSize,
        offset,
      };
      delete params.q;

      const payload = await adminService.getWaitlistWhatsapp(params);
      const normalized = normalizeWhatsappResponse(payload, page, pageSize);
      setWhatsappItems(normalized.items);
      setWhatsappPageInfo({
        totalPages: normalized.totalPages,
        totalCount: normalized.totalCount,
      });
      setWhatsappStats({
        totalRecords: normalized.totalRecords,
        validPhoneCount: normalized.validPhoneCount,
        invalidPhoneCount: normalized.invalidPhoneCount,
      });
    } catch (err) {
      console.error(err);
      setWhatsappError("Failed to fetch WhatsApp outreach list.");
    } finally {
      setWhatsappLoading(false);
    }
  }, [debouncedMessageTemplate, filters, page, pageSize]);

  useEffect(() => {
    if (activeView !== "waitlist") return;
    loadWaitlist();
  }, [activeView, loadWaitlist]);

  useEffect(() => {
    if (activeView !== "whatsapp") return;
    loadWhatsappWaitlist();
  }, [activeView, loadWhatsappWaitlist]);

  const currentItems = activeView === "waitlist" ? items : whatsappItems;
  const currentPageInfo = activeView === "waitlist" ? pageInfo : whatsappPageInfo;

  const sortedWaitlistItems = useMemo(() => {
    const getValue = WAITLIST_DATE_SORT_COLUMNS[waitlistSort.key];
    if (!getValue) return items;

    return items
      .map((item, index) => ({ item, index }))
      .sort((a, b) => {
        const aTime = getDateSortValue(getValue(a.item));
        const bTime = getDateSortValue(getValue(b.item));

        if (aTime === null && bTime === null) return a.index - b.index;
        if (aTime === null) return 1;
        if (bTime === null) return -1;
        if (aTime === bTime) return a.index - b.index;

        return waitlistSort.direction === "asc" ? aTime - bTime : bTime - aTime;
      })
      .map(({ item }) => item);
  }, [items, waitlistSort]);

  const derivedTotalPages = useMemo(() => {
    if (currentPageInfo.totalPages) return currentPageInfo.totalPages;
    if (currentPageInfo.totalCount) {
      return Math.max(1, Math.ceil(currentPageInfo.totalCount / pageSize));
    }
    return null;
  }, [currentPageInfo, pageSize]);

  const canPrev = page > 1;
  const canNext =
    derivedTotalPages !== null ? page < derivedTotalPages : currentItems.length === pageSize;

  const handleApplyFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      query: draftFilters.query,
      paid: draftFilters.paid,
      paymentStatus: draftFilters.paymentStatus,
    }));
  };

  const handleResetFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  const handleViewChange = (view) => {
    setActiveView(view);
    setPage(1);
    setDownloadStatus({ csv: "idle", vcf: "idle", message: "" });
  };

  const handleWaitlistSort = (key) => {
    setWaitlistSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getItemKey = useCallback(
    (item, index) => item.id || item.user_id || item.email || item.phone || index,
    []
  );

  const buildSignupUrl = useCallback(() => "https://www.bettercorporatelife.com/", []);

  const handleSendEmail = async (item, index) => {
    const email = String(item.email || "").trim();
    if (!email) return;

    const key = getItemKey(item, index);
    const name = String(item.name || getWaitlistName(item) || "").trim();
    const payload = { email, signup_url: buildSignupUrl(item) };
    if (name && name !== "Unknown") payload.name = name;

    setEmailStatus((prev) => ({ ...prev, [key]: { state: "sending", message: "" } }));

    try {
      await adminService.sendWaitlistEmail(payload);
      setEmailStatus((prev) => ({
        ...prev,
        [key]: { state: "sent", message: "", sentAt: new Date().toISOString() },
      }));
      toast.success("Email sent successfully.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message =
        (typeof detail === "object" ? detail?.message : detail) ||
        err?.message ||
        "Failed to send email.";
      setEmailStatus((prev) => ({ ...prev, [key]: { state: "error", message } }));
    }
  };

  const removeDeletedWaitlistUser = (waitlistUserId, rowKey) => {
    setItems((prev) =>
      prev.filter((currentItem, currentIndex) => {
        const currentWaitlistUserId = currentItem.id || currentItem.user_id;
        if (currentWaitlistUserId) return currentWaitlistUserId !== waitlistUserId;
        return getItemKey(currentItem, currentIndex) !== rowKey;
      })
    );
    setPageInfo((prev) => ({
      ...prev,
      totalCount:
        typeof prev.totalCount === "number" ? Math.max(0, prev.totalCount - 1) : prev.totalCount,
    }));
    setDeleteStatus((prev) => ({ ...prev, [rowKey]: { state: "deleted", message: "" } }));
    toast.success("Waitlist user deleted successfully.");
  };

  const handleDeleteClick = async (item, index) => {
    const rowKey = getItemKey(item, index);
    const waitlistUserId = item.id || item.user_id;
    const name = item.name || getWaitlistName(item);
    const email = String(item.email || "").trim();
    const label = email ? `${name} (${email})` : name;

    if (!waitlistUserId) {
      setDeleteStatus((prev) => ({
        ...prev,
        [rowKey]: { state: "error", message: "Missing waitlist user ID." },
      }));
      return;
    }

    setDeleteStatus((prev) => ({ ...prev, [rowKey]: { state: "deleting", message: "" } }));

    try {
      await adminService.deleteWaitlistUser(waitlistUserId);
      removeDeletedWaitlistUser(waitlistUserId, rowKey);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (
        err?.response?.status === 409 &&
        detail?.code === "WAITLIST_DELETE_CONFIRMATION_REQUIRED"
      ) {
        setDeleteStatus((prev) => ({ ...prev, [rowKey]: { state: "confirming", message: "" } }));
        setPendingDelete({ item, index, rowKey, waitlistUserId, label, related: detail.related });
        return;
      }

      const message =
        (typeof detail === "object" ? detail?.message : detail) ||
        err?.message ||
        "Failed to delete waitlist user.";
      setDeleteStatus((prev) => ({ ...prev, [rowKey]: { state: "error", message } }));
    }
  };

  const handleDeleteUser = async () => {
    if (!pendingDelete) return;

    const { rowKey, waitlistUserId } = pendingDelete;
    setDeleteStatus((prev) => ({ ...prev, [rowKey]: { state: "deleting", message: "" } }));

    try {
      await adminService.deleteWaitlistUser(waitlistUserId, true);
      removeDeletedWaitlistUser(waitlistUserId, rowKey);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message =
        (typeof detail === "object" ? detail?.message : detail) ||
        err?.message ||
        "Failed to delete waitlist user.";
      setDeleteStatus((prev) => ({ ...prev, [rowKey]: { state: "error", message } }));
    } finally {
      setPendingDelete(null);
    }
  };

  const handleDiagnosticsClick = async (item, index) => {
    const rowKey = getItemKey(item, index);
    const waitlistUserId = item.id;

    if (!waitlistUserId) {
      setDiagnosticModal({
        open: true,
        item,
        rowKey,
        session: null,
        loading: false,
        error: "Missing waitlist user ID.",
      });
      return;
    }

    setDiagnosticModal({
      open: true,
      item,
      rowKey,
      session: null,
      loading: true,
      error: "",
    });

    try {
      const payload = await adminService.getWaitlistBotSession(waitlistUserId);
      setDiagnosticModal((prev) => ({
        ...prev,
        session: normalizeDiagnosticSession(payload),
        loading: false,
      }));
    } catch (err) {
      const message =
        err?.response?.data?.detail || err?.message || "Failed to fetch diagnostic detail.";
      setDiagnosticModal((prev) => ({
        ...prev,
        loading: false,
        error: message,
      }));
    }
  };

  const handlePromotionFlowClick = async (item) => {
    const waitlistUserId = item.id;
    setPromotionFlowModal({
      open: true,
      item,
      session: null,
      loading: Boolean(waitlistUserId),
      error: waitlistUserId ? "" : "Missing waitlist user ID.",
    });
    if (!waitlistUserId) return;

    try {
      const payload = await adminService.getPromotionFlowSession(waitlistUserId);
      setPromotionFlowModal((prev) => ({ ...prev, session: normalizeDiagnosticSession(payload), loading: false }));
    } catch (err) {
      const message = err?.response?.data?.detail || err?.message || "Failed to fetch promotion flow detail.";
      setPromotionFlowModal((prev) => ({ ...prev, loading: false, error: message }));
    }
  };

  const handleWhatsappTemplateChange = (event) => {
    const nextValue = event.target.value;
    setPage(1);
    setFilters((prev) => ({ ...prev, messageTemplate: nextValue }));
    setDraftFilters((prev) => ({ ...prev, messageTemplate: nextValue }));
  };

  const handleWhatsappExport = async (type) => {
    const filename = type === "csv" ? "waitlist-whatsapp.csv" : "waitlist-whatsapp.vcf";
    const params = {
      ...buildWaitlistFilters({
        ...filters,
        messageTemplate: debouncedMessageTemplate,
      }),
    };
    delete params.q;

    setDownloadStatus((prev) => ({ ...prev, [type]: "loading", message: "" }));

    try {
      const blob =
        type === "csv"
          ? await adminService.downloadWaitlistWhatsappCsv(params)
          : await adminService.downloadWaitlistWhatsappVcf(params);
      triggerBlobDownload(blob, filename);
      setDownloadStatus((prev) => ({ ...prev, [type]: "done" }));
    } catch (err) {
      const message =
        err?.response?.data?.detail || err?.message || `Failed to download ${type.toUpperCase()}.`;
      setDownloadStatus((prev) => ({ ...prev, [type]: "error", message }));
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <h1 className="text-display">Waitlist</h1>
        <div className="inline-flex w-full rounded-lg bg-white p-1 shadow stakeholder-summary-card md:w-auto">
          {VIEW_OPTIONS.map((option) => {
            const isActive = activeView === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleViewChange(option.value)}
                className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition md:flex-none ${
                  isActive
                    ? "bg-[#54B0AF] text-white shadow"
                    : "text-[#246F6E] hover:bg-[#F5F9F9]"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
      {activeView === "waitlist" ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <StatCard label="Total waitlisted" value={pageInfo.totalCount ?? items.length} />
          <StatCard
            label="Paid (page)"
            value={items.filter((item) => item.paid === true).length}
          />
          <StatCard
            label="Unpaid (page)"
            value={items.filter((item) => item.paid === false).length}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <StatCard
            label="Total records"
            value={whatsappStats.totalRecords ?? whatsappItems.length}
          />
          <StatCard label="Valid phone count" value={whatsappStats.validPhoneCount} />
          <StatCard label="Invalid phone count" value={whatsappStats.invalidPhoneCount} />
        </div>
      )}

      <div className="bg-white rounded-lg shadow stakeholder-summary-card p-6 space-y-6">
        <form
          onSubmit={handleApplyFilters}
          className={`grid gap-4 items-end ${
            activeView === "whatsapp" ? "md:grid-cols-5" : "md:grid-cols-4"
          }`}
        >
          <div className="space-y-2 md:col-span-2">
            <label className="text-sm font-semibold text-title">Search</label>
            <input
              type="text"
              value={draftFilters.query}
              onChange={(e) =>
                setDraftFilters((prev) => ({ ...prev, query: e.target.value }))
              }
              placeholder="Name, email, or ID"
              className="w-full border rounded-md px-3 py-2 text-sm"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-title">Paid</label>
            <select
              value={draftFilters.paid}
              onChange={(e) =>
                setDraftFilters((prev) => ({ ...prev, paid: e.target.value }))
              }
              className="w-full border rounded-md px-3 py-2 text-sm"
            >
              <option value="">All</option>
              <option value="true">Paid</option>
              <option value="false">Unpaid</option>
            </select>
          </div>

          {activeView === "whatsapp" && (
            <div className="space-y-2">
              <label className="text-sm font-semibold text-title">Payment status</label>
              <input
                type="text"
                value={draftFilters.paymentStatus}
                onChange={(e) =>
                  setDraftFilters((prev) => ({
                    ...prev,
                    paymentStatus: e.target.value,
                  }))
                }
                placeholder="completed, pending..."
                className="w-full border rounded-md px-3 py-2 text-sm"
              />
            </div>
          )}

          <div className="flex gap-2 md:col-span-full">
            <button
              type="submit"
              className="px-4 py-2 rounded-md bg-[#54B0AF] text-white text-sm hover:opacity-90 transition shadow"
            >
              Apply filters
            </button>
            <button
              type="button"
              onClick={handleResetFilters}
              className="px-4 py-2 rounded-md border text-sm hover:bg-gray-50 transition"
            >
              Reset
            </button>
          </div>
        </form>

        {activeView === "whatsapp" && (
          <div className="rounded-lg border border-[#DCEEEE] bg-[#F8FBFB] p-4 space-y-4">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-title">Message template</label>
                <textarea
                  value={filters.messageTemplate}
                  onChange={handleWhatsappTemplateChange}
                  placeholder="Hi {{name}}, sharing your Stoc.ai access details..."
                  rows={3}
                  className="w-full border rounded-md px-3 py-2 text-sm resize-y bg-white"
                />
                <p className="text-xs text-muted">
                  WhatsApp links refresh automatically after edits.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleWhatsappExport("csv")}
                  disabled={downloadStatus.csv === "loading"}
                  className="px-4 py-2 rounded-md border text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                >
                  {downloadStatus.csv === "loading"
                    ? "Downloading CSV..."
                    : "Download WhatsApp CSV"}
                </button>
                <button
                  type="button"
                  onClick={() => handleWhatsappExport("vcf")}
                  disabled={downloadStatus.vcf === "loading"}
                  className="px-4 py-2 rounded-md border text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                >
                  {downloadStatus.vcf === "loading" ? "Downloading VCF..." : "Download VCF"}
                </button>
              </div>
            </div>

            {downloadStatus.message && (
              <div className="text-sm text-red-600">{downloadStatus.message}</div>
            )}
          </div>
        )}

        {activeView === "waitlist" && loading && <LoadingState label="Loading waitlist..." />}
        {activeView === "whatsapp" && whatsappLoading && (
          <LoadingState label="Loading WhatsApp outreach..." />
        )}
        {activeView === "waitlist" && error && <div className="text-red-600 text-sm">{error}</div>}
        {activeView === "whatsapp" && whatsappError && (
          <div className="text-red-600 text-sm">{whatsappError}</div>
        )}

        {activeView === "waitlist" && !loading && !error && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1280px] border-collapse text-[15px]">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th>Phone</Th>
                    <Th>Payment Status</Th>
                    <SortableTh
                      sortKey="created_at"
                      activeKey={waitlistSort.key}
                      direction={waitlistSort.direction}
                      onSort={handleWaitlistSort}
                    >
                      Added
                    </SortableTh>
                    <SortableTh
                      sortKey="paid_at"
                      activeKey={waitlistSort.key}
                      direction={waitlistSort.direction}
                      onSort={handleWaitlistSort}
                    >
                      Paid At
                    </SortableTh>
                    <Th>Waitlist Submit Count</Th>
                    <SortableTh
                      sortKey="first_seen_at"
                      activeKey={waitlistSort.key}
                      direction={waitlistSort.direction}
                      onSort={handleWaitlistSort}
                    >
                      First Seen
                    </SortableTh>
                    <SortableTh
                      sortKey="last_seen_at"
                      activeKey={waitlistSort.key}
                      direction={waitlistSort.direction}
                      onSort={handleWaitlistSort}
                    >
                      Last Seen
                    </SortableTh>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {sortedWaitlistItems.map((item, index) => {
                    const rowKey = getItemKey(item, index);
                    const emailState = emailStatus[rowKey]?.state || "idle";
                    const emailMessage = emailStatus[rowKey]?.message || "";
                    const deleteState = deleteStatus[rowKey]?.state || "idle";
                    const deleteMessage = deleteStatus[rowKey]?.message || "";
                    const isSending = emailState === "sending";
                    const isSent = emailState === "sent";
                    const isEmailError = emailState === "error";
                    const isDeleting = deleteState === "deleting";
                    const isDeleteError = deleteState === "error";
                    const name = item.name || getWaitlistName(item);
                    const email = item.email || "-";
                    const phone = item.phone || "-";
                    const submitCount = Number(item.waitlist_submit_count || 0);
                    const paymentStatus = getPaymentStatus(item);
                    const addedAt = formatDate(item.created_at);
                    const paidAt = formatDate(item.paid_at, true);
                    const firstSeenAt = formatDate(item.first_seen_at || item.created_at, true);
                    const lastSeenAt = formatDate(item.last_seen_at || item.created_at, true);
                    const canSendEmail = Boolean(item.email) && emailState !== "sending" && !isDeleting;
                    const canDelete = !isDeleting && !isSending;

                    return (
                      <tr key={rowKey} className="border-b hover:bg-gray-100 transition align-top">
                        <Td>{name}</Td>
                        <Td>{email}</Td>
                        <Td>{phone}</Td>
                        <Td>
                          <StatusPill>{paymentStatus}</StatusPill>
                        </Td>
                        <Td className="text-muted">{addedAt}</Td>
                        <Td className="text-muted">{paidAt}</Td>
                        <Td>{submitCount || "--"}</Td>
                        <Td className="text-muted">{firstSeenAt}</Td>
                        <Td className="text-muted">{lastSeenAt}</Td>
                        <Td>
                          <div className="flex flex-col gap-1">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => handleSendEmail(item, index)}
                                disabled={!canSendEmail}
                                className="px-3 py-1 rounded-md border text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                              >
                                {isSending ? "Sending..." : isSent ? "Resend email" : "Send email"}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDiagnosticsClick(item, index)}
                                disabled={isDeleting}
                                className="px-3 py-1 rounded-md border text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                              >
                                Diagnostics
                              </button>
                              <button
                                type="button"
                                onClick={() => handlePromotionFlowClick(item)}
                                disabled={isDeleting}
                                className="px-3 py-1 rounded-md border text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                              >
                                Promotion Flow
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteClick(item, index)}
                                disabled={!canDelete}
                                className="px-3 py-1 rounded-md border border-red-200 text-red-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-50 transition"
                              >
                                {isDeleting ? "Deleting..." : "Delete"}
                              </button>
                            </div>
                            {isSent && <span className="text-xs text-emerald-700">Email sent</span>}
                            {isEmailError && (
                              <span className="text-xs text-red-600">
                                {emailMessage || "Failed to send"}
                              </span>
                            )}
                            {isDeleteError && (
                              <span className="text-xs text-red-600">
                                {deleteMessage || "Failed to delete"}
                              </span>
                            )}
                            {!item.email && (
                              <span className="text-xs text-muted">No email on file</span>
                            )}
                          </div>
                        </Td>
                      </tr>
                    );
                  })}

                  {items.length === 0 && <EmptyRow colSpan="10" label="No waitlist entries found." />}
                </tbody>
              </table>
            </div>

            <PaginationBar
              page={page}
              totalCount={pageInfo.totalCount}
              derivedTotalPages={derivedTotalPages}
              canPrev={canPrev}
              canNext={canNext}
              pageSize={pageSize}
              onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
              onNext={() => setPage((prev) => prev + 1)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </>
        )}

        {activeView === "whatsapp" && !whatsappLoading && !whatsappError && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] border-collapse text-[15px]">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <Th>Name</Th>
                    <Th>Phone</Th>
                    <Th>Email</Th>
                    <Th>Source</Th>
                    <Th>Paid status</Th>
                    <Th>WhatsApp status</Th>
                    <Th>Action</Th>
                  </tr>
                </thead>
                <tbody>
                  {whatsappItems.map((item, index) => {
                    const rowKey = getItemKey(item, index);
                    const name = item.contact_name || getWaitlistName(item);
                    const phone = item.phone_raw || item.phone_digits || item.phone || "--";
                    const email = item.email || "--";
                    const source = item.source || "--";
                    const paidStatus = getCompactPaidStatus(item);
                    const isReady = item.is_whatsapp_ready === true;

                    return (
                      <tr key={rowKey} className="border-b hover:bg-gray-100 transition align-top">
                        <Td>{name}</Td>
                        <Td className="font-medium text-[#323232]">{phone}</Td>
                        <Td>{email}</Td>
                        <Td>{source}</Td>
                        <Td>
                          <StatusPill>{paidStatus}</StatusPill>
                        </Td>
                        <Td>
                          <span
                            className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                              isReady ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                            }`}
                          >
                            {isReady ? "Ready" : "Invalid phone"}
                          </span>
                        </Td>
                        <Td>
                          <div className="flex flex-col gap-1">
                            {isReady ? (
                              <a
                                href={item.whatsapp_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex w-fit items-center rounded-md bg-[#54B0AF] px-3 py-1.5 text-sm text-white hover:opacity-90 transition"
                              >
                                Open in WhatsApp
                              </a>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  disabled
                                  className="inline-flex w-fit items-center rounded-md border px-3 py-1.5 text-sm text-gray-400 cursor-not-allowed"
                                >
                                  Open in WhatsApp
                                </button>
                                <span className="text-xs text-muted">Invalid or missing number</span>
                              </>
                            )}
                          </div>
                        </Td>
                      </tr>
                    );
                  })}

                  {whatsappItems.length === 0 && (
                    <EmptyRow colSpan="7" label="No WhatsApp-ready waitlist records found." />
                  )}
                </tbody>
              </table>
            </div>

            <PaginationBar
              page={page}
              totalCount={whatsappPageInfo.totalCount}
              derivedTotalPages={derivedTotalPages}
              canPrev={canPrev}
              canNext={canNext}
              pageSize={pageSize}
              onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
              onNext={() => setPage((prev) => prev + 1)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </>
        )}
      </div>

      {pendingDelete && (
        <DeleteWaitlistUserModal
          label={pendingDelete.label}
          related={pendingDelete.related}
          isDeleting={deleteStatus[pendingDelete.rowKey]?.state === "deleting"}
          onCancel={() => {
            setDeleteStatus((prev) => ({
              ...prev,
              [pendingDelete.rowKey]: { state: "idle", message: "" },
            }));
            setPendingDelete(null);
          }}
          onConfirm={handleDeleteUser}
        />
      )}

      {diagnosticModal.open && (
        <WaitlistDiagnosticModal
          item={diagnosticModal.item}
          session={diagnosticModal.session}
          loading={diagnosticModal.loading}
          error={diagnosticModal.error}
          onClose={() =>
            setDiagnosticModal({
              open: false,
              item: null,
              rowKey: null,
              session: null,
              loading: false,
              error: "",
            })
          }
        />
      )}
      {promotionFlowModal.open && (
        <WaitlistPromotionFlowModal
          item={promotionFlowModal.item}
          session={promotionFlowModal.session}
          loading={promotionFlowModal.loading}
          error={promotionFlowModal.error}
          onClose={() => setPromotionFlowModal({ open: false, item: null, session: null, loading: false, error: "" })}
        />
      )}
    </div>
  );
};

export default Waitlist;

const normalizeWaitlistResponse = (payload, fallbackPage, fallbackPageSize) => {
  const items =
    (Array.isArray(payload) && payload) ||
    (Array.isArray(payload?.items) && payload.items) ||
    (Array.isArray(payload?.data) && payload.data) ||
    (Array.isArray(payload?.waitlist) && payload.waitlist) ||
    [];

  const totalPages =
    (payload?.total && payload?.limit ? Math.max(1, Math.ceil(payload.total / payload.limit)) : null) ||
    payload?.total_pages ||
    payload?.pagination?.total_pages ||
    payload?.meta?.total_pages ||
    null;

  const totalCount =
    payload?.total ||
    payload?.total_count ||
    payload?.pagination?.total_count ||
    payload?.meta?.total_count ||
    null;

  const page =
    (typeof payload?.offset === "number" && payload?.limit
      ? Math.floor(payload.offset / payload.limit) + 1
      : null) ||
    payload?.page ||
    payload?.pagination?.page ||
    fallbackPage;
  const pageSize =
    payload?.limit ||
    payload?.page_size ||
    payload?.pagination?.page_size ||
    fallbackPageSize;

  return { items, totalPages, totalCount, page, pageSize };
};

const normalizeWhatsappResponse = (payload, fallbackPage, fallbackPageSize) => {
  const normalized = normalizeWaitlistResponse(payload, fallbackPage, fallbackPageSize);
  const items = normalized.items;

  const totalRecords =
    payload?.total ||
    payload?.total_count ||
    payload?.stats?.total_records ||
    payload?.summary?.total_records ||
    payload?.meta?.total_count ||
    items.length;

  const validPhoneCount =
    payload?.valid_phone_count ||
    payload?.stats?.valid_phone_count ||
    payload?.summary?.valid_phone_count ||
    payload?.ready_count ||
    items.filter((item) => item.is_whatsapp_ready === true).length;

  const invalidPhoneCount =
    payload?.invalid_phone_count ||
    payload?.stats?.invalid_phone_count ||
    payload?.summary?.invalid_phone_count ||
    (typeof totalRecords === "number" && typeof validPhoneCount === "number"
      ? Math.max(0, totalRecords - validPhoneCount)
      : items.filter((item) => item.is_whatsapp_ready !== true).length);

  return { ...normalized, items, totalRecords, validPhoneCount, invalidPhoneCount };
};

const normalizeDiagnosticSession = (payload) => {
  if (!payload) return null;
  if (payload.data && !Array.isArray(payload.data)) return payload.data;
  if (payload.session && !Array.isArray(payload.session)) return payload.session;
  if (Array.isArray(payload.items)) return payload.items[0] || null;
  if (Array.isArray(payload.data)) return payload.data[0] || null;
  if (Array.isArray(payload)) return payload[0] || null;
  return payload;
};

const getWaitlistName = (item) => {
  const fullName =
    `${item.first_name || ""} ${item.last_name || ""}`.trim() ||
    item.name ||
    item.full_name;
  return fullName || "Unknown";
};

const formatDate = (value, includeTime = false) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return includeTime ? date.toLocaleString() : date.toLocaleDateString();
};

const getDateSortValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  const time = date.getTime();
  return Number.isNaN(time) ? null : time;
};

const parseJsonValue = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
};

const formatFieldLabel = (key) =>
  String(key || "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isEmptyData = (value) => {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  if (isPlainObject(value)) return Object.keys(value).length === 0;
  return false;
};

const formatPrimitiveValue = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const normalizeAnswerKey = (key) => String(key || "").replace(/[^a-z0-9]/gi, "").toLowerCase();

const findDirectValue = (value, aliases) => {
  if (!isPlainObject(value)) return undefined;
  const targetKeys = aliases.map(normalizeAnswerKey);
  const match = Object.entries(value).find(([key]) => targetKeys.includes(normalizeAnswerKey(key)));
  return match ? match[1] : undefined;
};

const findAnswerValue = (value, aliases) => {
  const parsedValue = parseJsonValue(value);
  const targetKeys = aliases.map(normalizeAnswerKey);

  if (Array.isArray(parsedValue)) {
    for (const entry of parsedValue) {
      const nestedValue = findAnswerValue(entry, aliases);
      if (!isEmptyData(nestedValue)) return nestedValue;
    }
    return undefined;
  }

  if (isPlainObject(parsedValue)) {
    for (const [key, entryValue] of Object.entries(parsedValue)) {
      if (targetKeys.includes(normalizeAnswerKey(key))) return entryValue;
    }

    for (const entryValue of Object.values(parsedValue)) {
      const nestedValue = findAnswerValue(entryValue, aliases);
      if (!isEmptyData(nestedValue)) return nestedValue;
    }
  }

  return undefined;
};

const cleanAnswerDisplayValue = (value) => {
  const parsedValue = parseJsonValue(value);

  if (Array.isArray(parsedValue)) {
    return parsedValue
      .map(cleanAnswerDisplayValue)
      .filter((entry) => !isEmptyData(entry));
  }

  if (isPlainObject(parsedValue)) {
    const responseText = findDirectValue(parsedValue, ANSWER_RESPONSE_ALIASES);
    const hasResponseTextKey = responseText !== undefined;
    if (hasResponseTextKey) {
      return isEmptyData(responseText) ? undefined : responseText;
    }

    const hasLabelKey = findDirectValue(parsedValue, ANSWER_LABEL_ALIASES) !== undefined;
    if (hasLabelKey) return undefined;

    return Object.entries(parsedValue).reduce((acc, [key, entryValue]) => {
      const cleanedValue = cleanAnswerDisplayValue(entryValue);
      if (!isEmptyData(cleanedValue)) acc[key] = cleanedValue;
      return acc;
    }, {});
  }

  return parsedValue;
};

const buildAnswerSummary = (answers) =>
  ANSWER_SUMMARY_FIELDS.map((field) => ({
    ...field,
    value: cleanAnswerDisplayValue(findAnswerValue(answers, field.aliases)),
  })).filter((field) => !isEmptyData(field.value));

const buildOrderedSummary = (value, fields) =>
  fields
    .map((field) => ({
      ...field,
      value: findAnswerValue(value, field.aliases),
    }))
    .filter((field) => !isEmptyData(field.value));

const getPaymentStatus = (item) => {
  if (item.paid === true) return "Paid";
  const status = item.payment_status || item.checkout_status || item.payment_link_status;
  if (status) {
    return String(status)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
  if (item.paid === false) return "Unpaid";
  return "Unknown";
};

const getCompactPaidStatus = (item) => {
  if (item.paid === true) return "Paid";
  if (item.paid === false) return "Unpaid";
  return "Unknown";
};

const LoadingState = ({ label }) => (
  <div className="flex items-center gap-2 text-[#54B0AF]">
    <Loader2 className="animate-spin" size={22} />
    {label}
  </div>
);

const Th = ({ children }) => (
  <th className="p-3 text-left text-title font-semibold">{children}</th>
);

const SortableTh = ({ children, sortKey, activeKey, direction, onSort }) => {
  const isActive = sortKey === activeKey;
  const Icon = isActive ? (direction === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  const label = `${children} ${isActive ? `sorted ${direction}` : "sortable"}`;

  return (
    <th
      className="p-3 text-left text-title font-semibold"
      aria-sort={isActive ? (direction === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        aria-label={label}
        className="inline-flex items-center gap-1.5 rounded text-left font-semibold hover:text-[#246F6E] focus:outline-none focus:ring-2 focus:ring-[#54B0AF] focus:ring-offset-2"
      >
        <span>{children}</span>
        <Icon size={14} aria-hidden="true" />
      </button>
    </th>
  );
};

const Td = ({ children, className = "" }) => <td className={`p-3 ${className}`}>{children}</td>;

const StatusPill = ({ children }) => (
  <span className="px-2 py-1 bg-[#E6F3F3] text-[#246F6E] rounded text-xs font-medium">
    {children}
  </span>
);

const EmptyRow = ({ colSpan, label }) => (
  <tr>
    <td colSpan={colSpan} className="text-center text-muted italic py-6">
      {label}
    </td>
  </tr>
);

const StatCard = ({ label, value }) => (
  <div className="bg-white shadow rounded-lg p-5 text-center stakeholder-summary-card">
    <p className="text-muted text-sm">{label}</p>
    <p className="text-2xl font-semibold text-[#323232] mt-1">{value}</p>
  </div>
);

const PaginationBar = ({
  page,
  totalCount,
  derivedTotalPages,
  canPrev,
  canNext,
  pageSize,
  onPrev,
  onNext,
  onPageSizeChange,
}) => (
  <div className="flex flex-col gap-3 pt-4 md:flex-row md:items-center md:justify-between">
    <div className="text-sm text-muted">
      {totalCount !== null ? `Total: ${totalCount}` : "Total: --"}
    </div>

    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onPrev}
        disabled={!canPrev}
        className="px-3 py-1 rounded-md border text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Prev
      </button>

      <span className="text-sm text-muted">
        Page {page}
        {derivedTotalPages ? ` of ${derivedTotalPages}` : ""}
      </span>

      <button
        type="button"
        onClick={onNext}
        disabled={!canNext}
        className="px-3 py-1 rounded-md border text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Next
      </button>
    </div>

    <div className="flex items-center gap-2">
      <span className="text-sm text-muted">Rows</span>
      <select
        value={pageSize}
        onChange={(e) => onPageSizeChange(Number(e.target.value))}
        className="border rounded-md px-2 py-1 text-sm"
      >
        {[10, 25, 50, 100].map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>
    </div>
  </div>
);

const DeleteWaitlistUserModal = ({ label, related, isDeleting, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
    <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-100 p-6">
      <div className="space-y-3">
        <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-red-50 text-red-600 text-lg font-semibold">
          !
        </div>
        <div>
          <h2 className="text-xl font-semibold text-title">Delete waitlist user?</h2>
          <p className="mt-2 text-sm leading-6 text-muted">
            {formatRelatedDeleteMessage(related)}
          </p>
          <p className="mt-2 text-xs text-muted">
            User: <span className="font-medium text-[#323232]">{label}</span>
          </p>
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isDeleting}
          className="px-4 py-2 rounded-md border text-sm hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={isDeleting}
          className="px-4 py-2 rounded-md bg-red-600 text-white text-sm hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isDeleting ? "Deleting..." : "Delete user"}
        </button>
      </div>
    </div>
  </div>
);

export const formatRelatedDeleteMessage = (related = {}) => {
  const counts = [
    ["consultation booking", related.consultation_bookings ?? related.consultation_booking_count],
    ["payment attempt", related.payment_attempts ?? related.payment_attempt_count],
    ["bot session", related.bot_sessions ?? related.bot_session_count],
  ]
    .map(([label, value]) => [label, Number(value || 0)])
    .filter(([, value]) => value > 0)
    .map(([label, value]) => `${value} ${label}${value === 1 ? "" : "s"}`);

  const summary =
    counts.length > 1
      ? `${counts.slice(0, -1).join(", ")}, and ${counts[counts.length - 1]}`
      : counts[0] || "related records";

  return `This user has ${summary}. Deleting the user will also delete these records. Continue?`;
};

const WaitlistDiagnosticModal = ({ item, session, loading, error, onClose }) => {
  const name = item?.name || getWaitlistName(item || {});
  const email = item?.email || "--";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-2xl bg-white shadow-2xl border border-slate-100">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-6 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-sm font-medium text-[#246F6E]">Waitlist diagnostics</p>
            <h2 className="mt-1 text-2xl font-semibold text-title">{name}</h2>
            <p className="mt-1 text-sm text-muted">{email}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="self-start rounded-md border px-3 py-1.5 text-sm hover:bg-gray-50 transition"
          >
            Close
          </button>
        </div>

        <div className="overflow-y-auto p-6">
          {loading && <LoadingState label="Loading diagnostic detail..." />}

          {!loading && error && (
            <div className="rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && !session && (
            <div className="rounded-lg border border-slate-100 bg-slate-50 p-6 text-sm text-muted">
              No diagnostic session was found for this waitlist user.
            </div>
          )}

          {!loading && !error && session && (
            <div className="space-y-6">
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                <DiagnosticField label="Status" value={session.status} />
                <DiagnosticField label="Current step" value={session.current_step} />
                <DiagnosticField label="Started" value={formatDate(session.started_at, true)} />
                <DiagnosticField label="Completed" value={formatDate(session.completed_at, true)} />
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <AnswerSummaryPanel value={session.answers_json} />
                <OrderedSummaryPanel title="Result" value={session.result_json} fields={RESULT_SUMMARY_FIELDS} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const WaitlistPromotionFlowModal = ({ item, session, loading, error, onClose }) => {
  const answers = session?.answers_json || {};
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl border border-slate-100 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-6">
          <div>
            <p className="text-sm font-medium text-[#246F6E]">Promotion flow</p>
            <h2 className="mt-1 text-2xl font-semibold text-title">{item?.name || getWaitlistName(item || {})}</h2>
            <p className="mt-1 text-sm text-muted">{item?.email || "--"}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md border px-3 py-1.5 text-sm transition hover:bg-gray-50">Close</button>
        </div>
        <div className="overflow-y-auto p-6">
          {loading && <LoadingState label="Loading promotion flow..." />}
          {!loading && error && <div className="rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
          {!loading && !error && session && (
            <div className="grid gap-3 md:grid-cols-2">
              <DiagnosticField label="Status" value={session.status} />
              <DiagnosticField label="Current screen" value={session.current_step} />
              <DiagnosticField label="Selected barrier" value={answers.barrier_label} />
              <DiagnosticField label="Selected fallback" value={answers.choice_label} />
              <DiagnosticField label="Started" value={formatDate(session.started_at, true)} />
              <DiagnosticField label="Completed" value={formatDate(session.completed_at, true)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DiagnosticField = ({ label, value }) => (
  <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
    <p className="text-xs font-semibold uppercase text-muted">{label}</p>
    <p className="mt-1 break-words text-sm font-medium text-[#323232]">{value || "--"}</p>
  </div>
);

const AnswerSummaryPanel = ({ value }) => {
  const fields = buildAnswerSummary(value);

  return (
    <div className="rounded-lg border border-slate-100 bg-[#F8FBFB]">
      <div className="border-b border-slate-100 px-4 py-3">
        <h3 className="text-sm font-semibold text-title">Answers</h3>
      </div>
      <div className="max-h-[28rem] overflow-auto p-4">
        {fields.length === 0 ? (
          <p className="text-sm text-muted">No answered diagnostic fields available.</p>
        ) : (
          <div className="space-y-3">
            {fields.map((field) => (
              <div key={field.label} className="rounded-lg border border-slate-100 bg-white p-3">
                <p className="text-xs font-semibold uppercase text-muted">{field.label}</p>
                <div className="mt-2">
                  <StructuredValue value={field.value} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const OrderedSummaryPanel = ({ title, value, fields: summaryFields }) => {
  const fields = buildOrderedSummary(value, summaryFields);

  return (
    <div className="rounded-lg border border-slate-100 bg-[#F8FBFB]">
      <div className="border-b border-slate-100 px-4 py-3">
        <h3 className="text-sm font-semibold text-title">{title}</h3>
      </div>
      <div className="max-h-[28rem] overflow-auto p-4">
        {fields.length === 0 ? (
          <p className="text-sm text-muted">No {title.toLowerCase()} data available.</p>
        ) : (
          <div className="space-y-3">
            {fields.map((field) => (
              <div key={field.label} className="rounded-lg border border-slate-100 bg-white p-3">
                <p className="text-xs font-semibold uppercase text-muted">{field.label}</p>
                <div className="mt-2">
                  <StructuredValue value={field.value} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const StructuredValue = ({ value }) => {
  if (Array.isArray(value)) {
    if (value.length === 0) return <p className="text-sm text-muted">No entries.</p>;

    const primitiveArray = value.every((entry) => !isPlainObject(entry) && !Array.isArray(entry));
    if (primitiveArray) {
      return (
        <div className="flex flex-wrap gap-2">
          {value.map((entry, index) => (
            <span
              key={`${entry}-${index}`}
              className="rounded-full border border-[#DCEEEE] bg-white px-3 py-1 text-xs font-medium text-[#246F6E]"
            >
              {formatPrimitiveValue(entry)}
            </span>
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {value.map((entry, index) => (
          <div key={index} className="rounded-lg border border-slate-100 bg-white p-3">
            <p className="mb-2 text-xs font-semibold uppercase text-muted">Entry {index + 1}</p>
            <StructuredValue value={entry} />
          </div>
        ))}
      </div>
    );
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value);
    if (entries.length === 0) return <p className="text-sm text-muted">No fields.</p>;

    return (
      <div className="space-y-3">
        {entries.map(([key, entryValue]) => {
          const hasNestedValue = isPlainObject(entryValue) || Array.isArray(entryValue);
          return (
            <div key={key} className="rounded-lg border border-slate-100 bg-white p-3">
              <p className="text-xs font-semibold uppercase text-muted">{formatFieldLabel(key)}</p>
              <div className="mt-2">
                {hasNestedValue ? (
                  <StructuredValue value={entryValue} />
                ) : (
                  <ValueText value={entryValue} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return <ValueText value={value} />;
};

const ValueText = ({ value }) => {
  if (typeof value === "boolean") {
    return (
      <span
        className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
          value ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"
        }`}
      >
        {formatPrimitiveValue(value)}
      </span>
    );
  }

  return (
    <p className="whitespace-pre-wrap break-words text-sm leading-6 text-[#323232]">
      {formatPrimitiveValue(value)}
    </p>
  );
};
